import Phaser from "phaser";

import { MinigameScene, type PanelCopy } from "./MinigameScene";
import { CloudLayer, SpeckleLayer } from "./Scenery";

// Campo mais largo que o do Flappy: em um corredor o que importa e enxergar o
// obstaculo com tempo de reagir, e isso e distancia horizontal. O que sobra de
// tela alem do campo vira ceu e areia (ver layoutView).
const VIEW_W = 260;
const VIEW_H = 140;
/** Linha do chao: onde ficam os pes do tatu e a base dos mandacarus. */
const GROUND_Y = 116;
/** Altura da faixa de chao com textura; abaixo dela a areia segue lisa. */
const GROUND_H = 16;

const SKY_DAY = 0x9cc4de;
const SKY_NIGHT = 0x161a2c;
const SAND_DAY = 0xd6be8e;
/** A areia da noite ja e a do dia multiplicada pela tonalidade noturna: sem
 * isso aparece uma emenda entre a faixa de chao e o resto do campo. */
const SAND_NIGHT = 0x767066;
const PEBBLE_DAY = 0xbaa070;
const PEBBLE_NIGHT = 0x686356;

const TATU_X = 40;
/**
 * Distancia entre o tatu e a borda direita da tela: e ela que da o tempo de
 * reagir ao obstaculo, entao e ela que precisa ser igual em toda tela. Em tela
 * larga o tatu anda para a direita e a sobra fica atras dele.
 */
const TATU_REACH = VIEW_W - TATU_X;
/** Meia largura da caixa de colisao do tatu (o sprite tem 24px). */
const TATU_HALF_W = 7;
/** Altura da caixa do tatu, contada a partir dos pes. */
const TATU_HEIGHT = 18;

// Salto de altura fixa, como no corredor original: dura 0,71s e sobe 57px, com
// folga para passar por cima do maior mandacaru. Nao existe "salto curto" — no
// celular o toque dura poucos milissegundos, e cortar o salto por isso tornaria
// o jogo impossivel de jogar com o dedo.
const GRAVITY = 900;
const JUMP_VELOCITY = -320;

// A velocidade inicial nao pode ser baixa demais: o salto tem duracao fixa,
// entao correr devagar significa passar mais tempo em cima do obstaculo e a
// janela de acerto encolhe justamente no comeco da partida.
const START_SPEED = 140;
const MAX_SPEED = 240;
/** Aceleracao por segundo: chega na velocidade maxima em ~50s. */
const SPEED_RAMP = 2;

/** Distancia percorrida que vale 1 ponto. */
const PX_PER_POINT = 22;
/** A cada tantos pontos o dia vira noite (e a noite vira dia). */
const NIGHT_EVERY = 500;
/** Antes disso so aparecem mandacarus: urubu no comeco e injusto. */
const VULTURE_FROM = 140;

/**
 * Alturas de voo do urubu, em pixels acima do chao (centro do bicho):
 *   alta  passa por cima do tatu de pe — quem salta e que se machuca
 *   baixa rasante: precisa saltar
 */
const VULTURE_HEIGHTS = [28, 6];

interface Obstacle {
  /** Centro horizontal, em pixels do campo. */
  x: number;
  halfW: number;
  /** Extremos verticais da caixa (topo menor que base, como na tela). */
  top: number;
  bottom: number;
  sprite: Phaser.GameObjects.Image | Phaser.GameObjects.Sprite;
}

/**
 * Minigame estilo "dino run" do Chrome, na caatinga: o tatu corre sozinho, a
 * velocidade sobe sem parar e o jogador so escolhe quando saltar. A pontuacao
 * e a distancia percorrida e vai para o mesmo ranking dos outros minigames.
 */
export class RunnerScene extends MinigameScene {
  private speed = START_SPEED;
  /** Distancia acumulada, em pixels — a pontuacao sai daqui. */
  private distance = 0;
  private nextObstacleAt = 0;

  private tatuY = GROUND_Y;
  /** Anda com a borda direita da tela; ver TATU_REACH. */
  private tatuX = TATU_X;
  private velocity = 0;
  private airborne = false;
  private night = false;

  private obstacles: Obstacle[] = [];
  private clouds!: CloudLayer;
  private stars!: SpeckleLayer;
  private pebbles!: SpeckleLayer;

  private sky!: Phaser.GameObjects.Rectangle;
  private tatu!: Phaser.GameObjects.Sprite;
  private ground!: Phaser.GameObjects.TileSprite;
  private dirt!: Phaser.GameObjects.Rectangle;

  constructor() {
    super("runner", VIEW_W, VIEW_H);
  }

  // ------------------------------------------------------------- montagem

  protected buildWorld(): void {
    // O Phaser reaproveita a instancia da cena a cada `launch`, e o shutdown
    // destroi os objetos mas nao esta lista: sem limpar, a segunda partida
    // comecaria com referencias mortas da primeira.
    this.obstacles = [];

    // Ceu, estrelas e nuvens ficam fora do container do campo: la a ordem de
    // desenho e a ordem de insercao, e os obstaculos entram depois. Aqui quem
    // resolve e o depth.
    this.sky = this.add.rectangle(0, 0, VIEW_W, VIEW_H, SKY_DAY).setOrigin(0, 0).setDepth(-20);
    // Estrelas: ficam escondidas de dia e aparecem quando a noite cai.
    this.stars = new SpeckleLayer(this, {
      depth: -19,
      width: 1,
      height: 1,
      color: 0xf4f2e2,
      density: 10,
      max: 90,
    });
    this.stars.setVisible(false);
    this.clouds = new CloudLayer(this, "runner-cloud", -18, 1.2);

    // Dentro do container a ordem de desenho e a ordem de insercao: o chao
    // primeiro, e os obstaculos entram na frente dele.
    this.ground = this.add.tileSprite(0, GROUND_Y, VIEW_W, GROUND_H, "runner-ground").setOrigin(0, 0);
    // Abaixo da faixa de chao a areia continua ate o fim da tela.
    this.dirt = this.add.rectangle(0, GROUND_Y + GROUND_H, VIEW_W, 1, SAND_DAY).setOrigin(0, 0);
    this.field.add([this.ground, this.dirt]);

    // Pedriscos quebram a areia lisa quando sobra muita tela embaixo do chao.
    this.pebbles = new SpeckleLayer(this, {
      depth: 1,
      width: 2,
      height: 1,
      color: PEBBLE_DAY,
      density: 5,
      max: 70,
    });

    // O tatu fica fora do container do campo pelo mesmo motivo do ceu: os
    // obstaculos entram depois dele e ficariam na frente.
    this.tatu = this.add.sprite(this.tatuX, GROUND_Y + 2, "runner-tatu", 0).setOrigin(0.5, 1).setDepth(3);
  }

  /**
   * Estica o cenario ate as bordas da tela. O campo continua do mesmo tamanho:
   * a sobra vira ceu (acima), areia (abaixo) e caatinga ja percorrida (a
   * esquerda do tatu).
   */
  protected override layoutView(): void {
    const view = this.view;

    this.sky.setPosition(view.left, view.top).setSize(view.width, view.height);
    this.ground.setPosition(view.left, GROUND_Y).setSize(view.width, GROUND_H);
    const sandTop = GROUND_Y + GROUND_H;
    this.dirt.setPosition(view.left, sandTop).setSize(view.width, Math.max(0, view.bottom - sandTop));

    this.stars.fit({ left: view.left, right: view.right, top: view.top + 4, bottom: GROUND_Y - 60 });
    this.clouds.fit({ left: view.left, right: view.right, top: view.top + 6, bottom: GROUND_Y - 55 });
    this.pebbles.fit({ left: view.left, right: view.right, top: sandTop + 2, bottom: view.bottom });

    // A tela mudou de largura: o tatu anda junto com a borda direita, e os
    // obstaculos vao junto para que a partida em andamento nao mude de figura.
    const tatuX = view.right - TATU_REACH;
    const shift = tatuX - this.tatuX;
    this.tatuX = tatuX;
    this.tatu.setX(tatuX);
    this.nextObstacleAt += shift;
    for (const obstacle of this.obstacles) {
      obstacle.x += shift;
      obstacle.sprite.setX(Math.round(obstacle.x));
    }
  }

  protected introPanel(): PanelCopy {
    return {
      text: `${this.params.label.toUpperCase()}\n\nSalte os mandacarus\ne os urubus.`,
      hint: this.params.touch.enabled
        ? "Toque para saltar"
        : "ESPAÇO salta\nESC volta para a cidade",
    };
  }

  /** Cinco digitos com zeros a esquerda, como no corredor original. */
  protected override formatScore(value: number): string {
    return String(value).padStart(5, "0");
  }

  // ------------------------------------------------------------- gameplay

  protected resetRun(): void {
    for (const obstacle of this.obstacles) obstacle.sprite.destroy();
    this.obstacles = [];

    this.speed = START_SPEED;
    this.distance = 0;
    this.nextObstacleAt = this.view.right + 60;
    this.tatuY = GROUND_Y;
    this.velocity = 0;
    this.airborne = false;
    this.setNight(false);
    this.syncTatu();
  }

  protected action(): void {
    this.jump();
  }

  private jump(): void {
    if (this.airborne) return;
    this.airborne = true;
    this.velocity = JUMP_VELOCITY;
  }

  protected advance(dt: number): void {
    this.speed = Math.min(this.speed + SPEED_RAMP * dt, MAX_SPEED);
    const shift = this.speed * dt;

    this.distance += shift;
    const points = Math.floor(this.distance / PX_PER_POINT);
    if (points !== this.score) {
      this.setScore(points);
      // O corredor original inverte as cores; aqui a caatinga anoitece.
      this.setNight(Math.floor(points / NIGHT_EVERY) % 2 === 1);
    }

    if (this.airborne) {
      this.velocity += GRAVITY * dt;
      this.tatuY += this.velocity * dt;
      if (this.tatuY >= GROUND_Y) {
        this.tatuY = GROUND_Y;
        this.velocity = 0;
        this.airborne = false;
      }
    }

    this.ground.tilePositionX += shift;
    this.clouds.scroll(shift);
    this.pebbles.scroll(shift);

    this.moveObstacles(shift);
    this.spawnObstacles();
    this.syncTatu();

    if (this.hits()) {
      this.tatu.setFrame(3);
      void this.fail();
    }
  }

  protected override idle(dt: number): void {
    if (this.phase === "ready") {
      // O tatu corre parado enquanto espera o primeiro comando.
      const shift = START_SPEED * 0.35 * dt;
      this.ground.tilePositionX += shift;
      this.clouds.scroll(shift);
      this.pebbles.scroll(shift);
      this.syncTatu();
    }
  }

  private syncTatu(): void {
    // +2 porque a ultima linha do frame e vazia: assim as patas encostam em
    // GROUND_Y e o desenho fica exatamente do tamanho da caixa de colisao.
    this.tatu.setY(Math.round(this.tatuY) + 2);

    if (this.phase === "dead") return; // congela no frame da batida
    if (this.airborne) {
      this.tatu.setFrame(2);
      return;
    }

    // A cadencia das patas acompanha a velocidade da corrida.
    const cycle = Math.floor(this.time.now / (this.phase === "playing" ? 7200 / this.speed : 110)) % 2;
    this.tatu.setFrame(cycle);
  }

  // ------------------------------------------------------------ obstaculos

  private moveObstacles(shift: number): void {
    this.nextObstacleAt -= shift;

    const survivors: Obstacle[] = [];
    for (const obstacle of this.obstacles) {
      obstacle.x -= shift;
      obstacle.sprite.setX(Math.round(obstacle.x));
      if (obstacle.x < this.view.left - 30) obstacle.sprite.destroy();
      else survivors.push(obstacle);
    }
    this.obstacles = survivors;
  }

  /**
   * Um obstaculo por vez, sempre a uma distancia proporcional a velocidade:
   * assim o tempo de reacao continua parecido do comeco ao fim da corrida.
   */
  private spawnObstacles(): void {
    // O obstaculo nasce fora da vista, seja ela larga ou estreita; o que garante
    // o mesmo tempo de reacao para todos e a distancia fixa ate o tatu.
    if (this.nextObstacleAt > this.view.right) return;

    const x = this.view.right + 20;
    const width =
      this.score >= VULTURE_FROM && Math.random() < 0.28 ? this.spawnVulture(x) : this.spawnCacti(x);

    // O vao comeca no fim do obstaculo, senao um grupo largo de mandacarus
    // chegaria colado no proximo.
    // O menor vao ainda precisa ser maior que um salto inteiro (0,71s), senao o
    // proximo obstaculo chega com o tatu no ar, sem chance de reagir.
    this.nextObstacleAt = x + width + this.speed * Phaser.Math.FloatBetween(0.8, 1.3) + 30;
  }

  /** Desenha o obstaculo e devolve a largura que ele ocupa. */
  private spawnCacti(x: number): number {
    const large = Math.random() < 0.35;
    const key = large ? "runner-cactus-large" : "runner-cactus-small";
    const width = large ? 17 : 9;
    const height = large ? 32 : 22;
    // Grupos de mandacaru: quanto mais rapido, mais largo o obstaculo pode ser.
    const max = large ? 2 : 3;
    const count = this.speed > START_SPEED * 1.4 ? Phaser.Math.Between(1, max) : 1;

    for (let n = 0; n < count; n++) {
      const cx = x + n * width;
      const sprite = this.add.image(cx, GROUND_Y + 1, key).setOrigin(0.5, 1);
      sprite.setTint(this.tint);
      this.field.add(sprite);
      this.obstacles.push({
        x: cx,
        // Caixa mais estreita que o desenho: os bracos do mandacaru nao contam,
        // senao o salto parece injusto.
        halfW: width / 2 - 2,
        top: GROUND_Y - height + 2,
        bottom: GROUND_Y,
        sprite,
      });
    }
    return count * width;
  }

  private spawnVulture(x: number): number {
    const above = VULTURE_HEIGHTS[Phaser.Math.Between(0, VULTURE_HEIGHTS.length - 1)];
    const y = GROUND_Y - above;
    const sprite = this.add.sprite(x, y, "runner-vulture", 0).setOrigin(0.5, 0.5);
    sprite.setTint(this.tint);
    sprite.play("runner-vulture-flap");
    this.field.add(sprite);

    this.obstacles.push({ x, halfW: 7, top: y - 4, bottom: y + 4, sprite });
    return 20;
  }

  private hits(): boolean {
    const top = this.tatuY - TATU_HEIGHT;

    for (const obstacle of this.obstacles) {
      if (this.tatuX + TATU_HALF_W <= obstacle.x - obstacle.halfW) continue;
      if (this.tatuX - TATU_HALF_W >= obstacle.x + obstacle.halfW) continue;
      if (top < obstacle.bottom && this.tatuY > obstacle.top) return true;
    }
    return false;
  }

  // ---------------------------------------------------------------- paleta

  /** Tonalidade aplicada aos sprites: de dia nenhuma, de noite um azul escuro. */
  private get tint(): number {
    return this.night ? 0x8d97b8 : 0xffffff;
  }

  /** Dia e noite: muda o ceu, acende as estrelas e escurece o resto. */
  private setNight(night: boolean): void {
    if (night === this.night) return;
    this.night = night;

    this.sky.setFillStyle(night ? SKY_NIGHT : SKY_DAY);
    this.dirt.setFillStyle(night ? SAND_NIGHT : SAND_DAY);
    this.pebbles.setColor(night ? PEBBLE_NIGHT : PEBBLE_DAY);
    this.stars.setVisible(night);

    this.ground.setTint(this.tint);
    this.tatu.setTint(this.tint);
    this.clouds.setTint(night ? 0x6d7690 : 0xffffff);
    for (const obstacle of this.obstacles) obstacle.sprite.setTint(this.tint);
  }
}
